import { z } from 'zod';
/** Shared primitives. Defined once so validation cannot drift per endpoint. */
export const uuidSchema = z.string().uuid();
export const cursorPageSchema = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
export function pageResultSchema(item) {
    return z.object({
        data: z.array(item),
        page: z.object({
            cursor: z.string().nullable(),
            nextCursor: z.string().nullable(),
            limit: z.number().int(),
            total: z.number().int().nullable(),
        }),
    });
}
/**
 * Time range. Refined rather than merely typed — an inverted range silently
 * returns an empty chart, which reads as "no data" and sends someone looking
 * for a collector fault that does not exist.
 */
export const timeRangeSchema = z
    .object({
    from: z.coerce.date(),
    to: z.coerce.date(),
})
    .refine((v) => v.to > v.from, { message: '`to` must be after `from`', path: ['to'] });
export const geoPointSchema = z.object({
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
});
export const bboxSchema = z
    .string()
    .regex(/^-?\d+(\.\d+)?(,-?\d+(\.\d+)?){3}$/, 'bbox must be "minLng,minLat,maxLng,maxLat"')
    .transform((s) => {
    const [minLng, minLat, maxLng, maxLat] = s.split(',').map(Number);
    return { minLng, minLat, maxLng, maxLat };
});
export const severitySchema = z.enum(['P1', 'P2', 'P3', 'P4']);
export const elementStatusSchema = z.enum([
    'UP',
    'DOWN',
    'DEGRADED',
    'UNREACHABLE',
    'MAINTENANCE',
    'UNKNOWN',
]);
/**
 * Boolean from a string source (env var, query parameter).
 *
 * **Never use `z.coerce.boolean()` for this.** It is `Boolean(value)`, so the
 * string `"false"` becomes `true` — as does `"0"`, `"no"`, and every other
 * value a human would write meaning false. That defect shipped in the first
 * deploy of this system and silently turned `UISP_WRITE_ENABLED=false` into
 * write access against the billing system.
 *
 * Unrecognised values are a validation error rather than a silent default:
 * a typo'd flag should stop the process, not pick a side.
 */
// The explicit return type matters. Without it the inferred ZodEffects type is
// too complex to survive declaration emit across the package boundary, and
// every consumer's `z.infer` degrades to unknown — which showed up as twenty
// unrelated "Type 'unknown' is not assignable" errors in the API's config.
export function booleanish(defaultValue) {
    const TRUE = new Set(['true', '1', 'yes', 'on']);
    const FALSE = new Set(['false', '0', 'no', 'off']);
    return z
        .union([z.boolean(), z.string()])
        .optional()
        .transform((value, ctx) => {
        if (typeof value === 'boolean')
            return value;
        if (value === undefined || value.trim() === '') {
            if (defaultValue === undefined) {
                ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Expected a boolean value' });
                return z.NEVER;
            }
            return defaultValue;
        }
        const normalised = value.trim().toLowerCase();
        if (TRUE.has(normalised))
            return true;
        if (FALSE.has(normalised))
            return false;
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Expected true/false (or 1/0, yes/no, on/off), received "${value}"`,
        });
        return z.NEVER;
    });
}
/**
 * Same parsing, but absence stays `undefined` rather than becoming a value.
 *
 * For a filter, "not supplied" and "supplied as false" are different requests:
 * `?slaBreached=false` means *show only unbreached*, while omitting it means
 * *do not filter on this at all*. Collapsing the two silently hides rows.
 */
export function optionalBooleanish() {
    return z
        .union([z.boolean(), z.string()])
        .optional()
        .transform((value, ctx) => {
        if (value === undefined || (typeof value === 'string' && value.trim() === '')) {
            return undefined;
        }
        if (typeof value === 'boolean')
            return value;
        const normalised = value.trim().toLowerCase();
        if (['true', '1', 'yes', 'on'].includes(normalised))
            return true;
        if (['false', '0', 'no', 'off'].includes(normalised))
            return false;
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Expected true/false (or 1/0, yes/no, on/off), received "${value}"`,
        });
        return z.NEVER;
    });
}
/** Comma-separated enum filter: `?filter[severity]=P1,P2`. */
export function csvEnum(values) {
    return z
        .string()
        .transform((s) => s.split(',').map((v) => v.trim()))
        .pipe(z.array(z.enum(values)));
}
//# sourceMappingURL=common.js.map